import { useCallback, useEffect, useState } from 'react';
import { SpecieService } from '../../services/specie/specieService';

/*----------------------------------------------------------------------------------------
* EN: Hook to manage species fetching and transformation logic.
* ES: Hook para gestionar la lógica de obtención y transformación de especies.
----------------------------------------------------------------------------------------*/

const useSpecies = (initialFilters: Record<string, unknown> = {}) => {

    const [data, setData] = useState<any[]>([]);
    const filterKey = JSON.stringify(initialFilters);
    const [filters, setFilters] = useState({
        limit: 999999,
        filter_filters: { ...initialFilters }
    });

    const fetchSpecies = useCallback(async () => {
        try {
            const service = new SpecieService();
            const response = await service.listForSelect({
                limit: 999999,
                filter_filters: { ...JSON.parse(filterKey) },
            });
            const fetchedData = response.getResponseData().data;

            if (fetchedData && fetchedData?.data) {
                const mappedData = fetchedData.data?.map(
                    (data: {
                        id: string;
                        commonName: string;
                        scientificName: string;
                        catAndalucia: boolean;
                        catNacional: boolean;
                        riskLevelAndalucia: string;
                        riskLevelNacional: string;
                    }) => ({
                        value: data.id,
                        label: data.commonName,
                        catAndalucia: data.catAndalucia,
                        commonName: data.commonName,
                        catNacional: data.catNacional,
                        scientificName: data.scientificName,
                        riskLevelAndalucia: data.riskLevelAndalucia ?? '-',
                        riskLevelNacional: data.riskLevelNacional ?? '-',
                    }));
                setData(mappedData);
            }
        } catch (error) {
            console.log('Error fetching species:', error);
        }
    }, [filterKey]);

    useEffect(() => {
        fetchSpecies();
    }, [fetchSpecies]);

    const sortByScientificName = (a: { scientificName?: string }, b: { scientificName?: string }) =>
        (a.scientificName ?? '').localeCompare(b.scientificName ?? '', 'es', { sensitivity: 'base' });

    const getSpeciesList = () => {
        return [...data].sort((a, b) =>
            (a.commonName ?? a.label ?? '').localeCompare(b.commonName ?? b.label ?? '', 'es', { sensitivity: 'base' })
        ).map((specie) => {
            let threatInfo = '';

            // Construir información de categorías de amenaza
            // TODO: añadir el tipo de amenaza EN / ES /...
            if (specie.catAndalucia && specie.catNacional) {
                threatInfo = ' (Regional: ' + specie.riskLevelAndalucia + ', Amenaza Nacional: ' + specie.riskLevelNacional + ')';
            } else if (specie.catAndalucia) {
                threatInfo = ' (Regional: ' + specie.riskLevelAndalucia + ')';
            } else if (specie.catNacional) {
                threatInfo = ' (Nacional: ' + specie.riskLevelNacional + ')';
            }

            return {
                ...specie,
                label: specie.label + threatInfo, // specie.label ya contiene el commonName
                originalLabel: specie.label, // Mantener el nombre original para búsquedas
            };
        });
    };

    const getSpeciesListScientific = () => {
        return [...data].sort(sortByScientificName).map((specie) => {
            let threatInfo = '';

            // Construir información de categorías de amenaza
            if (specie.catAndalucia && specie.catNacional) {
                threatInfo = ' (Regional: ' + specie.riskLevelAndalucia + ', Nacional: ' + specie.riskLevelNacional + ')';
            } else if (specie.catAndalucia) {
                threatInfo = ' (Regional: ' + specie.riskLevelAndalucia + ')';
            } else if (specie.catNacional) {
                threatInfo = ' (Nacional: ' + specie.riskLevelNacional + ')';
            }

            return {
                value: specie.value,
                label: specie.scientificName + threatInfo,
                catAndalucia: specie.catAndalucia,
                catNacional: specie.catNacional,
                scientificName: specie.scientificName,
                commonName: specie.commonName,
            };
        });
    };

    return { fetchSpecies, getSpeciesList, getSpeciesListScientific };
}

export default useSpecies;